import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import IntlComponent from 'axway-react-intl';
import { Accordion, Glyphicon, Col, ControlLabel, FormControl, FormGroup,
	HelpBlock, Row, Tabs, Tab } from 'react-bootstrap';
import { ContentMessage, MoreLess, Panel, Form } from 'axway-react-ui';
import InlineEdit from 'react-edit-inline';
import cn from 'classnames';
import NodeConfigField from './NodeConfigField';

const UNSUPPORTED_TYPES = [ 'null', 'boolean' ];

const compareParameters = (a, b) => {
	if (a.required && !b.required) {
		return -1;
	}
	if (!a.required && b.required) {
		return 1;
	}
	return a.name.localeCompare(b.name);
};

/**
 * A panel for configuring nodes.
 */
class NodeConfig extends React.Component {
	constructor(props) {
		super(props);
		IntlComponent.init(this);

		this.onParamEnable = this.onParamEnable.bind(this);
		this.onParamChange = this.onParamChange.bind(this);
		this.onParamDelete = this.onParamDelete.bind(this);
		this.onAuthorizationEnable = this.onAuthorizationEnable.bind(this);
		this.onAuthorizationChange = this.onAuthorizationChange.bind(this);
		this.onOutputChange = this.onOutputChange.bind(this);
		this.onOutputDelete = this.onOutputDelete.bind(this);
		this.onChangeNodeName = this.onChangeNodeName.bind(this);
		this.onChangeNodeMethod = this.onChangeNodeMethod.bind(this);
		this.getFieldValidation = this.getFieldValidation.bind(this);
		this.onConfigTabSelect = this.onConfigTabSelect.bind(this);
	}

	/**
	 * Toggles the enabling/disabling of a parameter.
	 *
	 * @param {string} paramId - The name of the param to be enabled/disabled.
	 * @param {boolean} enabled - The enable/disable switch
	 * @param {string} valueType - The input type of that parameter
	 * @param {string} value - The default value for that input type
	 */
	onParamEnable(paramId, enabled, valueType, value) {
		const { id, onParamEnable } = this.props;
		onParamEnable && onParamEnable({
			nodeId: id, paramId, enabled, type: 'param', valueType, value
		});
	}

	/**
	 * Triggers the removal of the output with the specified name from the current node's
	 * definition.
	 *
	 * @param {string} output The id of the output to be removed.
	 */
	onOutputDelete(output) {
		if (!output) {
			return;
		}

		const { id, onOutputDelete } = this.props;

		onOutputDelete && onOutputDelete({
			nodeId: id,
			output
		});
	}

	/**
	 * Method to change a particular parameter's value
	 * @param  {string} paramName - The parameter name. Needed for searching the params array
	 * @param  {string} key - The key in the parameter to change (e.g. type|value).
	 * @param  {any} value - The value to set on the parameter.value
	 */
	onParamChange(paramName, key, value) {
		if (!paramName) {
			return;
		}
		const { onConfigChange } = this.props;
		onConfigChange && onConfigChange({
			type: 'param',
			name: paramName,
			prop: key === 'valueType' ? 'type' : 'value',
			newValue: value
		});
	}

	/**
	 * Helper method that triggers the removal of the parameter with the specified parameter id
	 * from the current node's definition.
	 *
	 * @param {string} paramId The parameter's id that is to be removed.
	 */
	onParamDelete(paramId) {
		if (!paramId) {
			return;
		}

		const { id, onParamDelete } = this.props;

		onParamDelete && onParamDelete({
			nodeId: id,
			paramId
		});
	}

	/**
	 * Method to update an outputs value
	 * @param  {string} outputName - The name of the ouput to update
	 * @param  {string} key - The key in the parameter to change (e.g. type|value).
	 * @param  {string} value - The value the output context should be set to
	 */
	onOutputChange(outputName, key, value) {
		if (!outputName || key === 'valueType') {
			return;
		}
		const { onConfigChange } = this.props;
		onConfigChange && onConfigChange({
			type: 'output',
			name: outputName,
			newValue: value
		});
	}

	/**
	 * Authorization parameter enable switch was toggled.
	 * @param {string} paramId - The parameter id
	 * @param {boolean} enabled - The current enabled state.
	 */
	onAuthorizationEnable(paramId, enabled) {
		const { id, onParamEnable } = this.props;
		onParamEnable && onParamEnable({ nodeId: id, paramId, enabled, type: 'authz' });
	}

	/**
	 * Method to change an authorization parameter's value
	 * @param  {string} paramName - The parameter name. Needed for searching the params array
	 * @param  {string} key - The key in the parameter to change (e.g. type|value).
	 * @param  {any} value - The value to set on the parameter.value
	 */
	onAuthorizationChange(paramName, key, value) {
		if (!paramName) {
			return;
		}
		const { onConfigChange } = this.props;
		onConfigChange && onConfigChange({
			type: 'authz',
			name: paramName,
			prop: key === 'valueType' ? 'type' : 'value',
			newValue: value
		});
	}

	/**
	 * Method to change the node's name property
	 * @param  {string} name - The name to change the name to
	 */
	onChangeNodeName(name) {
		if (!name || !name.name) {
			return;
		}
		const { onConfigChange } = this.props;
		onConfigChange && onConfigChange({
			type: 'name',
			newValue: name.name
		});
	}

	/**
	 * Method to change the method of the node
	 * @param  {string} method - The method to switch to.
	 */
	onChangeNodeMethod(method) {
		if (!method) {
			return;
		}
		const { onConfigChange } = this.props;
		onConfigChange && onConfigChange({
			type: 'method',
			newValue: method
		});
	}

	/**
	 * Checks if the specific field has an invalidation
	 * @param  {string} type - outputsInvalid or paramsInvalid or authorizationInvalid
	 * @param  {string} name - Name of the field to check if invalid
	 * @return {object}           Return object if invalid
	 */
	getFieldValidation(type, name) {
		const { validationMap, id } = this.props;
		// get node errors
		let retVal = validationMap && validationMap.nodeErrors && validationMap.nodeErrors[id];

		if (retVal && type) {
			// get errors of specific type
			retVal = retVal[type];
			if (retVal && name) {
				// get errors for this specific item
				retVal = retVal[name];
			}
		}
		return retVal;
	}

	renderTitleBar() {
		const { node, nodehandler } = this.props;

		let icon = 'icon-GG-code';
		let iconType = 'icon';
		if (nodehandler && nodehandler.icon) {
			icon = nodehandler.icon;
			if (icon && icon.match(/^data:image\/(?:bmp|jpeg|png|gif|tiff|svg\+xml);base64,.*/)) {
				iconType = 'img';
			}
		}

		const invalid = this.getFieldValidation();
		return (
			<div className="node-title">
				<div className={`node-name${invalid ? ' error' : ''}`}>
					{icon && iconType === 'icon' && (
						<Glyphicon glyph={` gg ${(icon)}`} />
					)}
					{icon && iconType === 'img' && (
						<img src={icon} alt="" />
					)}
					<InlineEdit
						className="text"
						activeClassName="active-text"
						text={node.name}
						paramName="name"
						change={this.onChangeNodeName}
					/>
					<Glyphicon glyph=" gg icon-GG-pencil" />
				</div>
			</div>
		);
	}

	renderPanelNode() {
		const { node, nodehandler, validationMap, id } = this.props;

		const flowNodeMethod = nodehandler && nodehandler.methods[node.method];
		const title = this.renderTitleBar();
		const methods = Object.keys(nodehandler.methods).sort();
		const invalidMethod = validationMap && validationMap.nodeErrors
			&& validationMap.nodeErrors[id] && validationMap.nodeErrors[id].methodInvalid;
		const options =	methods.map(
			(value) => (
				<option
					key={value}
					value={value}
				>
					{nodehandler.methods[value].name || value}
				</option>
			)
		);

		const description = flowNodeMethod && flowNodeMethod.description;

		if (!node.method) {
			options.push(
				<option key="none" value="" disabled style={{ display: 'none' }}>
					{this.intl.formatMsg('Select a method')}
				</option>
			);
		} else if (!flowNodeMethod) {
			options.push(
				<option key="none" value="" disabled style={{ display: 'none' }}>
					{this.intl.formatMsg('{name} (invalid)', { name: node.metaMethod })}
				</option>
			);
		}

		return (
			<Panel
				key="panel-node"
				header={title}
				className="panel-node"
			>
				<Row>
					<Col componentClass={ControlLabel} sm={3}>
						{this.intl.formatMsg('Type')}:
					</Col>
					<Col sm={9}>
						<FormControl.Static className="nodeconfig-name">
							{nodehandler.name}
						</FormControl.Static>
					</Col>
				</Row>
				<Row>
					<FormGroup
						controlId="typeCtrl"
						className="nodeconfig-method-group"
						validationState={invalidMethod ? 'error' : null}
					>
						<Col componentClass={ControlLabel} sm={3}>
							{this.intl.formatMsg('Method')}:
						</Col>
						<Col sm={9}>
							<FormControl
								className="nodeconfig-method"
								componentClass="select"
								onChange={(e) => this.onChangeNodeMethod(e.target.value)}
								value={(flowNodeMethod && node.method) || ''}
							>
								{options}
							</FormControl>
						</Col>
						{ invalidMethod && node.method && !flowNodeMethod && (
							<Col sm={12}>
								<HelpBlock className="nodeconfig-help">
									{this.intl.formatMsg('An update to this flow-node caused the current method to be renamed or removed. A replacement should be selected.')}
								</HelpBlock>
							</Col>
						)}
					</FormGroup>
				</Row>
				{description && (
					<Row>
						<Col sm={12}>
							<MoreLess
								className="nodeconfig-description"
								expanded={false}
							>
								{ description }
							</MoreLess>
						</Col>
					</Row>
				)}
			</Panel>
		);
	}

	onConfigTabSelect(topic, ev) {
		ev.stopPropagation();
		const { onConfigTopicChange, id } = this.props;
		if (onConfigTopicChange) {
			// This is a bad interface.  This really should only emit that a
			// tab changed.
			onConfigTopicChange({
				type: 'flow-node',
				id,
				topic
			});
		}
	}

	/**
	 * Render the contents of the parameters tab
	 */
	renderParametersTab(authz, params) {
		if (authz && params) {
			return [
				authz,
				params
			];
		} else if (authz) {
			return authz;
		} else if (params) {
			return params;
		} else {
			return this.renderEmpty(this.intl.formatMsg('No configurable parameters.'));
		}
	}

	/**
	 * Render the tabbed configuration section.
	 */
	renderPanelConfig() {
		const { node, nodehandler, topic } = this.props;
		const flowNodeMethod = nodehandler && nodehandler.methods[node.method];

		if (!node.method) {
			return this.renderEmpty(this.intl.formatMsg('Select a method to configure this node.'));
		} else if (!flowNodeMethod) {
			return this.renderEmpty(this.intl.formatMsg('Fix the highlighted errors in order to configure this node.'), 'warning');
		} else {
			const authz = this.renderAuthorizations();
			const params = this.renderParameters();
			const outputs = this.renderOutputs();

			if (!authz && !outputs && !params) {
				return this.renderEmpty(this.intl.formatMsg('Node requires no configuration.'));
			} else {
				const nodeErrors = this.getFieldValidation();

				const invalidParams = nodeErrors && (
					!!nodeErrors.paramsInvalid || !!nodeErrors.authorizationInvalid);
				const invalidOuputs = nodeErrors && !!nodeErrors.outputsInvalid;

				const parameterTab = (
					<div>
						{this.intl.formatMsg('Parameters') }{ invalidParams
							? <span className="node-status-icon gg icon-GG-warning" /> : ''
						}
					</div>
				);
				const outputTab = (
					<div>
						{this.intl.formatMsg('Outputs')}{ invalidOuputs
							? <span className="node-status-icon gg icon-GG-warning" /> : ''
						}
					</div>
				);

				return (
					<Panel
						key="panel-config"
						className="panel-config"
					>
						<Tabs
							id="config-tabs"
							className="axui tabs"
							animation={false}
							activeKey={topic}
							onSelect={this.onConfigTabSelect}
						>
							<Tab eventKey="parameters" title={parameterTab}>
								{ this.renderParametersTab(authz, params) }
							</Tab>
							<Tab eventKey="outputs" title={outputTab}>
								{ outputs || this.renderEmpty(this.intl.formatMsg('No configurable outputs.')) }
							</Tab>
						</Tabs>
					</Panel>
				);
			}
		}
	}

	/**
	 * Render an empty message if the node handler has no
	 * configurable parameters.
	 */
	renderEmpty(msg, icon = 'empty-list') {
		return (
			<ContentMessage
				detail={msg || this.intl.formatMsg('No configurable parameters.')}
				imgClass={`gg icon-GG-${icon}`}
				className="sm empty"
				inlineContent
			/>
		);
	}

	/**
	 * Render the configuration panel with the nodehandlers
	 * configParameters schema.
	 */
	renderOutputs() {
		const { node, nodehandler } = this.props;
		const flowNodeMethod = nodehandler && nodehandler.methods[node.method];
		const methodOutputs = flowNodeMethod && flowNodeMethod.outputs;
		const methodOutputIds = Object.keys(methodOutputs);
		if (!methodOutputs || !methodOutputIds.length) {
			return;
		}

		// This is a special case for HTTP.setResponse where we don't want to allow configuration
		// of the output. At some stage this may become an attribute of the spec but for now this
		// is the only instance.
		if (node.type === 'nodehandler://axway-flow/http' && node.method === 'setresponse') {
			return;
		}
		const outputs = [];
		// Collect any unknown outputs and render them first
		const invalidOutputs = this.getFieldValidation('outputsInvalid');
		for (const outputId in (invalidOutputs || {})) {
			const invalid = invalidOutputs[outputId];
			const output = node.outputs[outputId];
			// if we already updated the node we need to make sure that we have the invalidOutput
			// before adding to the outputs object
			if (output && invalid === 'NOT_IN_SPEC') {
				outputs.push(this.getOutputProps(
					outputId,
					output.metaName,
					output.metaDescription,
					output.schema,
					'OUTPUT_NOT_IN_SPEC',
					true));
			}
		}

		outputs.push(...methodOutputIds.map(outputId => {
			const invalid = this.getFieldValidation('outputsInvalid', outputId);
			const outputSpec = methodOutputs[outputId];
			return this.getOutputProps(
				outputId,
				outputSpec.name,
				outputSpec.description,
				outputSpec.schema,
				invalid
			);
		}));

		return outputs.map(params => (
			<NodeConfigField {...params} />
		));
	}
	getOutputProps(outputId, name, description, schema, invalid, deletable = false) {
		const { node, selectors } = this.props;
		return {
			id: outputId,
			key: outputId,
			name: name || outputId,
			schema: this.expandRef(schema) || {},
			description,
			label: this.intl.formatMsg('Save output value as'),
			value: node.outputs[outputId].context,
			valueType: 'jsonpath',
			onChange: this.onOutputChange,
			selectors,
			inputTypes: [ 'jsonpath' ],
			invalid,
			onDelete: this.onOutputDelete,
			showSchema: true,
			readOnly: deletable,
			deletable
		};
	}

	expandRef(schema) {
		const { schemas } = this.props;
		if (schema && schema.$ref && schemas.hasOwnProperty(schema.$ref)) {
			schema = schemas[schema.$ref];
		} else if (schema && schema.oneOf) {
			// RDPP-5432: We are adding support for oneOf.
			// If schema is of type oneOf search for reference within objects.
			const types = schema.oneOf;
			types.forEach((type, index) => {
				if (type.$ref && schemas.hasOwnProperty(type.$ref)) {
					types[index] = schemas[type.$ref];
				}
			});
		}
		return schema;
	}

	renderAuthorizations() {
		const { node, nodehandler, selectors, credentials } = this.props;

		const flowNodeMethod = nodehandler && nodehandler.methods[node.method];

		if (!flowNodeMethod
			|| !flowNodeMethod.authorization
			|| !flowNodeMethod.authorization.properties
			|| !Object.keys(flowNodeMethod.authorization.properties).length) {
			return;
		}

		// Map the configured node parameters by name
		const authorizations = node.authorizations || [];
		const paramsByID = authorizations.reduce(
			(col, cur) => {
				col[cur.name] = cur;
				return col;
			},
			{}
		);

		const defaultDescription = this.intl.formatMsg('Authorization parameter');
		const params = Object.keys(flowNodeMethod.authorization.properties)
			.map((paramID) => {
				const paramSpec = flowNodeMethod.authorization.properties[paramID];
				const schema = this.expandRef(paramSpec);
				const invalid = this.getFieldValidation('authorizationInvalid', paramID);
				return {
					id: paramID,
					key: paramID,
					name: paramSpec.title || paramID,
					required: flowNodeMethod.authorization.required
						&& flowNodeMethod.authorization.required.indexOf(paramID) !== -1,
					schema: schema || paramSpec || {},
					description: paramSpec.description || defaultDescription,
					enabled: !!(paramsByID && paramsByID.hasOwnProperty(paramID)),
					value: paramsByID && paramsByID[paramID] && paramsByID[paramID].value,
					// Note that the concept of initialType introduced with RDPP-5892 is not
					// applicable for Authorization parameters.
					valueType: (paramsByID && paramsByID[paramID] && paramsByID[paramID].type)
						|| 'jsonpath',
					valueFormat: paramSpec.format,
					onChange: this.onAuthorizationChange,
					onEnable: this.onAuthorizationEnable,
					selectors,
					invalid,
					credentials
				};
			});

		return params.sort(compareParameters).map(param => {
			return (
				<NodeConfigField {...param} />
			);
		});
	}
	/**
	 * Builds a map of parameters keyed from the name
	 *
	 * @param {Object} parameters A an array of parameters from a flow.
	 *
	 * @returns {Object} key: Parameter name (id), value: The value of the parameter
	 */
	getParameterMap(parameters) {
		let info = {};

		for (const param of parameters) {
			info[param.name] = param;
		}

		return info;
	}

	/**
	 * Builds a list of all the unknown parameters that can be resolved in the UI.
	 * Unknown parameters are displayed in the same way as the rest of the method parameters, with
	 * the following restrictions:
	 *  - Not editable
	 *  - Red background to draw attention to the problem
	 *  - Can be deleted
	 * @returns A list of all parameters that are in the flow but not the flow-node.
	 * An example of parameter that is considered invalid in that sense is one that no longer
	 * exist - either due to renaming or deletion. The user is required to manually delete it from
	 * the UI to "fix" the problem and is presented with the ability to copy it's old value.
	 */
	getUnknownParameters() {
		const { validationMap, id, selectors, node } = this.props;

		const nodeErrors = validationMap
			&& validationMap.nodeErrors
			&& validationMap.nodeErrors[id];
		// All the unknown parameters in the current node have NOT_IN_SPEC errors.
		const invalidParams = nodeErrors && nodeErrors.paramsInvalid;

		// If there aren't any invalid parameters - Great!
		if (!invalidParams) {
			return [];
		}
		/**
		 * A map of parameter names and values. Use the parameter's ID to retrieve the parameter
		 *   * const parameter = nodeParameterMap[paramID];
		 */
		const paramsByID = this.getParameterMap(node.parameters);

		return Object.keys(invalidParams)
			// Filter out only the unknown parameters
			.filter(paramID => invalidParams[paramID] === 'NOT_IN_SPEC')
			.map((paramID) => { // paramID is the ID of the parameter
				const parameter = paramsByID[paramID];

				// Check if the parameter
				if (!parameter) {
					return null;
				}

				const { value, type, metaName, metaDescription } = parameter;

				return {
					id: paramID,
					key: paramID,
					name: metaName,
					required: false,
					schema: {},
					enabled: true,
					value,
					valueType: type || 'jsonpath',
					description: metaDescription,
					selectors,
					invalid: 'PARAM_NOT_IN_SPEC',
					// Makes the field readOnly
					readOnly: true,
					// Applies the corresponding styles for unknown parameters.
					deletable: true,
					onDelete: this.onParamDelete
				};
			}).filter(param => param);
	}

	renderParameters() {
		const { node, nodehandler, selectors } = this.props;
		const flowNodeMethod = nodehandler && nodehandler.methods[node.method];

		if (!flowNodeMethod || !flowNodeMethod.parameter || !flowNodeMethod.parameter.properties
				|| !Object.keys(flowNodeMethod.parameter.properties).length) {
			return;
		}

		// Map the node parameters by name
		const paramsByID = node && this.getParameterMap(node.parameters);

		const validParams = Object.keys(flowNodeMethod.parameter.properties).map((id) => {
			const name = id;
			const paramSpec = flowNodeMethod.parameter.properties[name];
			const schema = this.expandRef(paramSpec);
			const invalid = this.getFieldValidation('paramsInvalid', name);
			const { type, value } = paramsByID && { type: 'jsonpath', ...paramsByID[name] };
			const required = flowNodeMethod.parameter.required
				&& flowNodeMethod.parameter.required.includes(name);

			// If we have no value then we should pick the type to show
			// If the spec defines an initialType we will use that if we can
			const initialType = schema.options && schema.options.initialType;

			let valueType = (initialType && value === undefined)
				? initialType : type;
			// This code checks `valueType` for an edge case where we cannot
			// render an enum with a missing value.  E.g. if we had a string
			// enum of "red", "green", "blue", and the value is undefined and
			// the initialType is string, then ideally, we'd render the
			// drop-down.  However, value is undefined, so there is no actual
			// enum value we could pick in order to render the drop-down.
			// Best we can do is switch the type (e.g. jsonpath).  The same
			// initial value issue exists for null and boolean.
			if (required && initialType) {
				// Do not allow certain types since we're not able to correctly
				// display a "missing value" for their type of input.  We only
				// care about enums types that we currently support rendering
				// as a drop-down.
				if ((schema.hasOwnProperty('enum')
					&& NodeConfigField.supportedEnumTypes.includes(valueType))
					|| UNSUPPORTED_TYPES.includes(valueType)) {
					// Revert back to using the type from the flow if the
					// `initialType` is not allowed
					valueType = type;
				}
			}

			return {
				id: name,
				key: name,
				name: paramSpec.title || name,
				required,
				schema: schema || paramSpec || {},
				description: paramSpec.description,
				group: paramSpec.group,
				enabled: !!(paramsByID && paramsByID.hasOwnProperty(name)),
				value: value,
				valueType,
				valueFormat: paramSpec.format,
				onChange: this.onParamChange,
				onEnable: this.onParamEnable,
				selectors,
				invalid
			};
		});

		const unknownParams = this.getUnknownParameters();

		const params = [ ...validParams ].reduce((acc, cur) => {
			// reduce the parameters into '_general' group, and by group name
			if (cur.group) {
				if (!acc[cur.group]) {
					acc[cur.group] = [];
				}
				if (!acc.groups[cur.group]) {
					acc.groups[cur.group] = [];
				}
				acc.groups[cur.group].push(cur);
				acc.groups[cur.group].sort(compareParameters);
			} else {
				acc._general.push(cur);
				acc._general.sort(compareParameters);
			}
			return acc;
		}, {
			_general: [],
			groups: {}
		});

		// will always have params._general, but it may be empty array
		const generalParams = [
			...unknownParams,
			...params._general
		].map((param) => <NodeConfigField {...param} />);

		const groups = Object.keys(params.groups).sort();
		if (!groups.length) {
			// no groups
			return generalParams;
		} else {
			// render grouped parameters
			const panels = generalParams;

			groups.forEach((name) => {
				// expand the group if any parameter is enabled, has a value, or is invalid
				const expand = !!params.groups[name]
					.find(a => a.enabled || a.required || a.value || a.invalid);
				const error = !!params.groups[name].find(a => a.invalid);
				const header = (
					<span key={name} className={cn('group-header', { error })}>
						{name}
						{ error && <a className="gg icon-GG-warning" /> }
					</span>
				);
				panels.push(
					<Accordion id={name} key={name}>
						<Panel
							independent
							hasIcon
							defaultExpanded={expand}
							header={header}
							bsStyle="default"
							animate
							borders={false}
						>
							{ params.groups[name].map((param) => <NodeConfigField {...param} />) }
						</Panel>
					</Accordion>
				);
			});
			return panels;
		}
	}

	render() {
		return (
			<div className="nodeconfig">
				<Form horizontal className="nodeconfig-form">
					{ this.renderPanelNode() }
					{ this.renderPanelConfig() }
				</Form>
			</div>
		);
	}
}

NodeConfig.propTypes = {
	/*
	 * The flow spec for the selected node
	 */
	node: PropTypes.object.isRequired,
	/*
	 * The flow-node spec for the selected node
	 */
	nodehandler: PropTypes.object,
	/*
	 * All the known schemas.
	 */
	schemas: PropTypes.object,
	/*
	 * The area of the config being displayed.
	 * Can be either 'outputs' or 'parameters'
	 */
	topic: PropTypes.string,
	/*
	 * Callback when a parameter is enabled.
	 */
	onParamEnable: PropTypes.func,
	/**
	 * A flow validation status object.
	 */
	validationMap: PropTypes.shape({
		valid: PropTypes.bool,
		nodeErrors: PropTypes.object
	}),
	/*
	 * Callback when a parameter value or type is changed..
	 */
	onConfigChange: PropTypes.func,
	/*
	 * The area of the config being displayed changed.
	 */
	onConfigTopicChange: PropTypes.func,

	selectors: PropTypes.arrayOf(PropTypes.string).isRequired,
	/*
	 * The available credentials that can be set on a node
	 */
	credentials: PropTypes.object
};

export default injectIntl(NodeConfig);
